import { Injectable } from '@angular/core';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { from, Observable, of } from 'rxjs';
import { catchError, filter, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { ChatService } from '../chat.service';
import {
  ChatActionTypes,
  ChatLogin,
  ChatLoginFailure,
  ChatLoginSuccess,
  CreateChannelsFromDistributionGroups,
  JoinRoom,
  OutgoingMessage,
  StatusChange
} from './actions';

@Injectable()
export class ChatEffects {
  constructor (
    private actions$: Actions,
    private chatService: ChatService
  ) {
  }

  @Effect()
  login$: Observable<ChatLoginSuccess | ChatLoginFailure> = this.actions$.pipe(
    ofType<ChatLogin>(ChatActionTypes.LOGIN),
    switchMap(({ payload }) => {
      this.chatService.initClient(payload);
      return from(this.chatService.chatClient.connect()).pipe(
        switchMap(jid => of(new ChatLoginSuccess({ jid, timestamp: Date.now() }))),
        catchError(() => of(new ChatLoginFailure({ timestamp: Date.now() })))
      );
    })
  );

  @Effect()
  joinRoomsOnOnlineOrNewChannelsAdded: Observable<JoinRoom> = this.actions$.pipe(
    filter<CreateChannelsFromDistributionGroups | StatusChange>(action => (
      action.type === ChatActionTypes.CREATE_CHANNELS_FROM_DISTRIBUTION_GROUPS && action.payload.groups.length
      || action.type === ChatActionTypes.STATUS_CHANGE && action.payload === 'online')
    ),
    withLatestFrom(this.chatService.userJid$, this.chatService.channels$),
    filter(([, jid]) => jid),
    switchMap(([, userJid, channels]) => of(
      ...channels.map(({ jid, history }) => {
        let since;
        if (history.length) {
          since = new Date(history[history.length - 1].timestamp.valueOf() + 1).toISOString();
        }
        return new JoinRoom({ roomJid: jid, userNick: userJid.bare().toString(), since });
      })
    ))
  );

  @Effect({ dispatch: false })
  joinRoom$: Observable<any> = this.actions$.pipe(
    ofType(ChatActionTypes.JOIN_ROOM),
    tap<JoinRoom>(({ payload: { roomJid, userNick, since } }) => {
      this.chatService.chatClient.joinRoom(roomJid, { nick: userNick, since });
    })
  );

  @Effect({ dispatch: false })
  outgoingMessage$: Observable<any> = this.actions$.pipe(
    ofType(ChatActionTypes.OUTGOING_MESSAGE),
    tap<OutgoingMessage>(({ payload: { message, to } }) => {
      this.chatService.chatClient.send(message, to.jid, { group: to.groupchat });
    })
  );
}
